import omit from 'lodash/omit';
import type React from 'react';
import { useMemo } from 'react';

import {
  isEmptyCompositionBucket,
  isOtherCompositionBucket,
  OTHER_BUCKET_LABEL,
} from '@revelio/core/composition-buckets';
import type {
  CompositionV3DataQuery,
  CompositionV3Ent,
  CompositionV3Req,
  PayV3Req,
} from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import {
  FilterType,
  type PrimaryFilterId,
} from '../../../filters/types/filters';
import {
  type CompositionV3BreakdownDimension,
  getCompositionV3BreakdownOptions,
} from '../../../filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import {
  getCompositionV3Variables,
  getCustomSkillDeepestLevelFromFilters,
  normalizeCompositionEffectiveFilters,
} from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { mergeDrilldownIntoCompositionV3Filters } from '../../../filters/utils/composition-v3/merge-drilldown-into-composition-v3-filters';
import {
  isParentNarrowableSegment,
  narrowCompositionV3FiltersToParents,
} from '../../../filters/utils/composition-v3/narrow-composition-v3-filters-to-parents';
import {
  deriveNestedTreemapView,
  getBreakdownLevelField,
} from '../../../filters/utils/composition-v3/treemapNestedLevels';
import { resolveV3EntForSelection } from '../../../filters/utils/get-v3-entities';
import { getMonthRefsFromV3Filters } from '../../../filters/utils/get-v3-month-refs';
import { getPayV3Variables } from '../../../filters/utils/pay-v3/get-pay-v3-variables';
import { stripEntitiesFromFilters } from '../../../filters/utils/role-skill-ent-kind';
import { ROLE_FINE_TAXONOMY_TREE_LEVELS } from '../../../filters/utils/taxonomy-tree-levels';
import {
  isOverlappingTreemapSegment,
  type RoleLadder,
  type TreemapDrilldownSegment,
} from '../../../filters/utils/treemapDrilldownLevels';
import {
  getRenderedParentIds,
  type NestedTreemapParent,
  type NestedTreemapParentTotal,
  nestedTreemapLookupKey,
  taxonomyCoverageMaxGroups,
} from '../../Chart/utils/buildNestedTreemapData';
import { useCompositionV3QueryWithStatus } from '../../DataResolver/use-composition-v3-query';
import type { TreemapTaxonomyCoverage } from '../../PageConfigProvider/types/item';
import { usePayData } from '../compensation/use-pay-data';
import { TOP_N_LIMIT } from '../constants';
import { getTopCompaniesFromPay } from '../TopNDataProvider';
import { DataProviderQueryStatus } from '../types';
import { combineQueryStatus } from '../utils/data-provider-results-utils';
import {
  type CompositionV3FullMetrics,
  compositionV3DataQueryFor,
  isCompositionV3FullMetrics,
} from './composition-v3/composition-v3-graphql-response';
import { pointAtSnapshot } from './composition-v3/convert-composition-data-v3';
import { useNestedTreemapParentLookup } from './composition-v3/useNestedTreemapParentLookup';
import { keepTopDrawnCells } from './keep-top-drawn-cells';
import {
  isOverviewSharedSegment,
  useOverviewSharedRequest,
  withOverviewSharedBreakdowns,
} from './overview-shared-request';

type CompositionV3Payload = NonNullable<
  CompositionV3DataQuery['composition_v3']
>;

export type DrilldownFilter =
  | {
      segment: 'geography';
      level: 'region' | 'country' | 'state' | 'msa' | 'city' | 'county';
      id: number;
    }
  | {
      segment: 'role';
      levelId:
        | 'role_k7'
        | 'role_k150'
        | 'role_k1500'
        | 'role_k5000'
        | 'role_k15000';
      id: string;
    }
  | {
      segment: 'rics';
      level: 'rics_k10' | 'rics_k50' | 'rics_k400' | 'company';
      id: number;
    }
  | {
      segment: 'skills';
      level: 'skill_0' | 'skill_1' | 'skill_2' | 'skill_3' | 'skill_4';
      id: string;
    }
  | {
      segment: 'activities';
      level:
        | 'activity_k150'
        | 'activity_k500'
        | 'activity_k1500'
        | 'activity_k5000';
      id: number;
    };

const SEGMENT_TO_TOP_N_FIELD: Record<
  TreemapDrilldownSegment,
  | 'geography_top_n'
  | 'role_top_n'
  | 'rics_top_n'
  | 'skill_top_n'
  | 'activities_top_n'
> = {
  geography: 'geography_top_n',
  role: 'role_top_n',
  rics: 'rics_top_n',
  skills: 'skill_top_n',
  activities: 'activities_top_n',
};

const SEGMENT_TO_V3_BREAKDOWN_DIMENSION: Record<
  DrilldownFilter['segment'],
  CompositionV3BreakdownDimension
> = {
  geography: 'geography',
  role: 'role',
  rics: 'rics',
  skills: 'skill',
  activities: 'activity',
};

type SegmentBucket = NonNullable<
  CompositionV3FullMetrics[TreemapDrilldownSegment]
>[number];

function mapSegmentBuckets(
  data: CompositionV3Payload | undefined,
  segment: TreemapDrilldownSegment,
  transformBuckets: (buckets: readonly SegmentBucket[]) => SegmentBucket[]
): CompositionV3Payload | undefined {
  if (data === undefined) return data;
  return {
    ...data,
    entities: (data.entities ?? []).map((entity) => {
      const buckets = isCompositionV3FullMetrics(entity.metrics)
        ? entity.metrics[segment]
        : undefined;
      if (!Array.isArray(buckets)) return entity;
      return {
        ...entity,
        metrics: {
          ...entity.metrics,
          [segment]: transformBuckets(buckets),
        },
      };
    }),
  };
}

function filterOutOverlappingOtherBucket(
  data: CompositionV3Payload | undefined,
  segment: TreemapDrilldownSegment
): CompositionV3Payload | undefined {
  if (!isOverlappingTreemapSegment(segment)) return data;
  return mapSegmentBuckets(data, segment, (buckets) =>
    buckets.filter((bucket) => !isOtherCompositionBucket(bucket.id))
  );
}

function stripBucketShares(
  data: CompositionV3Payload | undefined,
  segment: TreemapDrilldownSegment
): CompositionV3Payload | undefined {
  return mapSegmentBuckets(data, segment, (buckets) =>
    buckets.map((bucket) => ({
      ...bucket,
      headcount: bucket.headcount.map((point) => ({
        ...point,
        share: null as unknown as number,
      })),
    }))
  );
}

const DEFAULT_BREAKDOWN_TOP_N = 7;

function addParentTotal(
  totals: Map<string, NestedTreemapParentTotal>,
  key: string,
  name: string,
  count: number,
  share: number | null
): void {
  const existing = totals.get(key);
  const mergedShare = (() => {
    if (existing === undefined) return share;
    if (existing.share === null || share === null) return null;
    return existing.share + share;
  })();
  totals.set(key, {
    name: existing?.name ?? name,
    value: (existing?.value ?? 0) + count,
    share: mergedShare,
  });
}

export type NestedTreemapEntityParentTotals = {
  name: string;
  totals: Map<string, NestedTreemapParentTotal>;
};

type Props = {
  primaryDimension: PrimaryFilterId;
  segment: TreemapDrilldownSegment;
  /** Active drilldown, or `null` for the initial (top-level) treemap. */
  drilldown: DrilldownFilter | null;
  roleLadder?: RoleLadder;
  taxonomyCoverage?: TreemapTaxonomyCoverage;
  /**
   * Overrides the themed-appearance default. Pass `false` for consumers that
   * drill flat views (e.g. the stacked bar) and never want the grouped view
   * or its parent-totals query.
   */
  nestingEnabled?: boolean;
  children: (result: {
    data: CompositionV3Payload | undefined;
    monthRefs: string[];
    status: DataProviderQueryStatus;
    /** Child→parent grouping lookup; `null` when the view renders flat. */
    parentLookup: Map<string, NestedTreemapParent> | null;
    parentTotals: Map<string, NestedTreemapParentTotal>;
    parentTotalsByEntity: Map<string, NestedTreemapEntityParentTotals>;
    isNested: boolean;
    isChildDataPending: boolean;
    /** Level id pushed onto the drilldown stack on sub-rectangle click. */
    displayLevelId: string;
    isDisplayLeaf: boolean;
    /** Level id pushed on group-header click; `null` when not nested. */
    parentLevelId: string | null;
  }) => React.ReactNode;
};

export function NestedCompositionTreemapDrilldownProvider({
  primaryDimension,
  segment,
  drilldown,
  roleLadder = 'standard',
  taxonomyCoverage,
  nestingEnabled: nestingEnabledProp,
  children,
}: Props) {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const {
    activeFilterMap,
    usesStateBreakdownOverMsa,
    geographySelectionListIds,
    geographyTaxonomyId,
    skillTaxonomyId,
    skillTaxonomyVersion,
  } = useFilterContext();
  const { customSkillTree, isCustomSkillTaxonomyPending } =
    useCustomSkillTaxonomyTree(skillTaxonomyId, skillTaxonomyVersion);
  const nestingEnabled = nestingEnabledProp ?? true;

  const baseVariables = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters,
    isSnapshot: true,
    getSelectionBreakdowns: true,
    geographySelectionListIds,
    customSkillTree,
    activeFilterMap,
  });

  const mergedFilters =
    drilldown === null
      ? baseVariables.filters
      : mergeDrilldownIntoCompositionV3Filters(
          baseVariables.filters,
          drilldown
        );

  const normalizedEffectiveForBreakdown =
    normalizeCompositionEffectiveFilters(effectiveFilters);

  const breakdownCustomSkillDeepestLevel =
    drilldown?.segment === 'skills'
      ? drilldown.level
      : getCustomSkillDeepestLevelFromFilters(normalizedEffectiveForBreakdown);

  const isRicsCompanyBreakdown =
    drilldown?.segment === 'rics' &&
    (drilldown.level === 'rics_k400' || drilldown.level === 'company');

  const naturalBreakdownOptions = getCompositionV3BreakdownOptions({
    compositionFilters: mergedFilters,
    customSkillTree,
    customSkillDeepestLevel: breakdownCustomSkillDeepestLevel,
    includedBreakdownDimensions: [SEGMENT_TO_V3_BREAKDOWN_DIMENSION[segment]],
    geographySelectionListIds,
  });
  const naturalLevel =
    naturalBreakdownOptions[getBreakdownLevelField(segment)] ?? null;

  const {
    childDepth,
    breakdownLevelOverrides,
    parentEntKind,
    parentBreakdownLevelOverrides,
    displayLevelId,
    isDisplayLeaf,
    parentLevelId,
  } = deriveNestedTreemapView({
    segment,
    naturalLevel,
    geographySelectionListIds,
    isRicsCompanyBreakdown,
    nestingEnabled,
    roleLadder,
  });

  const breakdownOptions = getCompositionV3BreakdownOptions({
    compositionFilters: mergedFilters,
    customSkillTree,
    customSkillDeepestLevel: breakdownCustomSkillDeepestLevel,
    includedBreakdownDimensions: [SEGMENT_TO_V3_BREAKDOWN_DIMENSION[segment]],
    geographySelectionListIds,
    breakdownLevelOverrides,
  });

  const compositionQuery = compositionV3DataQueryFor(segment);
  const sharedRequest = useOverviewSharedRequest();
  const sharedCellsRequest =
    sharedRequest !== null &&
    drilldown === null &&
    isOverviewSharedSegment(sharedRequest, segment)
      ? sharedRequest
      : null;
  const sharesCellsRequest = sharedCellsRequest !== null;

  const { parentLookup, fetching: parentLookupFetching } =
    useNestedTreemapParentLookup({
      geographyTaxonomyId,
      segment,
      childDepth,
      customSkillTree,
      customSkillTreeFetching: isCustomSkillTaxonomyPending,
      roleTreeLevels:
        roleLadder === 'fine' ? ROLE_FINE_TAXONOMY_TREE_LEVELS : undefined,
    });

  const isNested = parentLookup !== null && childDepth !== null;

  const topNField = SEGMENT_TO_TOP_N_FIELD[segment];
  const topN = taxonomyCoverage?.topN;

  const parentBreakdownOptions = getCompositionV3BreakdownOptions({
    compositionFilters: mergedFilters,
    customSkillTree,
    customSkillDeepestLevel: breakdownCustomSkillDeepestLevel,
    includedBreakdownDimensions: [SEGMENT_TO_V3_BREAKDOWN_DIMENSION[segment]],
    geographySelectionListIds,
    breakdownLevelOverrides: parentBreakdownLevelOverrides,
  });

  const parentQueryActive =
    isNested && parentLevelId !== null && parentEntKind !== null;

  const parentRequest: CompositionV3Req = {
    ...baseVariables,
    filters: mergedFilters,
    breakdown_options:
      topN === undefined
        ? parentBreakdownOptions
        : { ...parentBreakdownOptions, [topNField]: topN.groups },
  };
  const [{ data: parentData, status: parentStatus }] =
    useCompositionV3QueryWithStatus<CompositionV3DataQuery>({
      query: sharedRequest ? sharedRequest.query : compositionQuery,
      variables: {
        req: sharedRequest
          ? withOverviewSharedBreakdowns(parentRequest, sharedRequest)
          : parentRequest,
      },
      pause: !parentQueryActive,
    });

  const { parentTotals, parentTotalsByEntity } = useMemo(() => {
    const totals = new Map<string, NestedTreemapParentTotal>();
    const byEntity = new Map<string, NestedTreemapEntityParentTotals>();
    const entities = parentData?.composition_v3?.entities ?? [];
    for (const entity of entities) {
      const entityTotals = new Map<string, NestedTreemapParentTotal>();
      const metrics = isCompositionV3FullMetrics(entity.metrics)
        ? entity.metrics
        : undefined;
      const buckets = metrics?.[segment] ?? [];
      const entityHeadcount = entity.metrics.headcount;
      const snapshotId = entityHeadcount[entityHeadcount.length - 1]?.id;
      for (const bucket of buckets) {
        if (isEmptyCompositionBucket(bucket?.id)) {
          continue;
        }
        const point = pointAtSnapshot(bucket.headcount, snapshotId);
        if (!point) continue;
        const key = nestedTreemapLookupKey(bucket.id);
        const share = typeof point.share === 'number' ? point.share : null;
        const rawName = bucket.short_name ?? '';
        const name = isOtherCompositionBucket(bucket.id)
          ? OTHER_BUCKET_LABEL
          : `${rawName.charAt(0).toUpperCase()}${rawName.slice(1)}`;
        addParentTotal(totals, key, name, point.count, share);
        addParentTotal(entityTotals, key, name, point.count, share);
      }
      const entityName = entity.short_name ?? '';
      byEntity.set(String(entity.id), {
        name: `${entityName.charAt(0).toUpperCase()}${entityName.slice(1)}`,
        totals: entityTotals,
      });
    }
    return { parentTotals: totals, parentTotalsByEntity: byEntity };
  }, [parentData, segment]);

  const scopesCellsToParents =
    parentQueryActive && isParentNarrowableSegment(segment);
  const canNarrowToParents = scopesCellsToParents && !sharesCellsRequest;

  const renderedParentIds = useMemo(
    () =>
      scopesCellsToParents
        ? getRenderedParentIds(
            parentTotals,
            taxonomyCoverageMaxGroups(taxonomyCoverage)
          )
        : [],
    [scopesCellsToParents, parentTotals, taxonomyCoverage]
  );

  const parentTotalsPending =
    scopesCellsToParents && parentStatus === DataProviderQueryStatus.Fetching;
  const awaitingParentTotals = canNarrowToParents && parentTotalsPending;

  const childFilters = narrowCompositionV3FiltersToParents({
    filters: mergedFilters,
    segment,
    parentEntKind,
    parentIds: canNarrowToParents ? renderedParentIds : [],
    geographySelectionListIds,
    roleLadder,
  });
  const isParentNarrowed = childFilters !== mergedFilters;

  const drilldownVariables = {
    ...baseVariables,
    filters: childFilters,
    breakdown_options:
      topN === undefined
        ? breakdownOptions
        : { ...breakdownOptions, [topNField]: topN.cells },
  };

  const primaryFilter = effectiveFilters.find((f) => f.isPrimary);
  const primaryEntities =
    primaryFilter?.type === FilterType.TREE
      ? primaryFilter.selections
          .map((selection) => resolveV3EntForSelection(selection))
          .filter((ent): ent is CompositionV3Ent => ent !== null)
      : [];

  const isRicsDrilldownActive =
    isRicsCompanyBreakdown &&
    drilldown !== null &&
    drilldown.segment === 'rics';

  const basePayVariables = getPayV3Variables({
    primaryDimension,
    effectiveFilters,
    entitiesOverride: primaryEntities,
  });
  const topNReq: PayV3Req = {
    ...basePayVariables,
    filters: isRicsDrilldownActive
      ? { ...basePayVariables.filters, rics_k400: [drilldown.id] }
      : basePayVariables.filters,
    ...(usesStateBreakdownOverMsa && {
      options: { geo_breakdown_state_over_msa: true },
    }),
  };

  const { data: topNData, status: topNStatus } = usePayData({
    variant: 'full',
    req: topNReq,
    pause: !isRicsCompanyBreakdown || primaryEntities.length === 0,
  });

  const topEntities =
    isRicsCompanyBreakdown && topNData?.pay_v3
      ? getTopCompaniesFromPay(topNData.pay_v3)?.slice(0, TOP_N_LIMIT)
      : undefined;

  const compositionVariables = (() => {
    if (
      isRicsCompanyBreakdown &&
      topEntities != null &&
      topEntities.length > 0
    ) {
      const filtersWithoutRics = omit(mergedFilters, [
        'rics_k10',
        'rics_k50',
        'rics_k400',
      ]);
      const entities = topEntities;
      return {
        ...drilldownVariables,
        filters: stripEntitiesFromFilters(filtersWithoutRics, entities),
        entities,
      };
    }
    return drilldownVariables;
  })();

  const compositionPause =
    awaitingParentTotals ||
    (childDepth !== null &&
      !sharesCellsRequest &&
      isParentNarrowableSegment(segment) &&
      parentLookupFetching) ||
    (isRicsCompanyBreakdown && (!topEntities || topEntities.length === 0));

  const [{ data: childData, status: compositionStatus }] =
    useCompositionV3QueryWithStatus<CompositionV3DataQuery>({
      query: sharedCellsRequest ? sharedCellsRequest.query : compositionQuery,
      variables: {
        req: sharedCellsRequest
          ? withOverviewSharedBreakdowns(
              compositionVariables,
              sharedCellsRequest,
              { tier: 'cells', owns: segment }
            )
          : compositionVariables,
      },
      pause: compositionPause,
    });

  const status = (() => {
    if (parentLookupFetching || parentTotalsPending) {
      return DataProviderQueryStatus.Fetching;
    }
    if (isRicsCompanyBreakdown) {
      return combineQueryStatus([topNStatus, compositionStatus]);
    }
    if (
      isNested &&
      parentStatus === DataProviderQueryStatus.Success &&
      getRenderedParentIds(parentTotals).length > 0
    ) {
      return DataProviderQueryStatus.Success;
    }
    return compositionStatus;
  })();

  // Paused counts as pending: the hook carries the previous request's payload
  // forward until the new one settles, and while paused it reports that stale
  // payload as Success.
  const isChildDataPending =
    isNested &&
    (compositionPause ||
      compositionStatus === DataProviderQueryStatus.Fetching);

  const monthRefs = getMonthRefsFromV3Filters(compositionVariables.filters);

  const cellsLimit = topN?.cells ?? DEFAULT_BREAKDOWN_TOP_N;
  const shapedChildData = useMemo(() => {
    const v21 = childData?.composition_v3;
    if (!v21) {
      return undefined;
    }
    const drawnParentIds = new Set(renderedParentIds);
    const drawnGroupOf = (bucketId: string | number) => {
      if (parentLookup === null || drawnParentIds.size === 0) return '';
      const parent = parentLookup.get(nestedTreemapLookupKey(bucketId));
      return parent !== undefined && drawnParentIds.has(parent.parentId)
        ? parent.parentId
        : null;
    };
    const scoped = (() => {
      if (sharesCellsRequest) {
        return keepTopDrawnCells(v21, segment, cellsLimit, drawnGroupOf);
      }
      return isParentNarrowed ? stripBucketShares(v21, segment) : v21;
    })();
    return filterOutOverlappingOtherBucket(scoped, segment);
  }, [
    childData,
    isParentNarrowed,
    segment,
    sharesCellsRequest,
    cellsLimit,
    renderedParentIds,
    parentLookup,
  ]);

  const finalData = (() => {
    if (
      isRicsCompanyBreakdown &&
      (compositionPause ||
        (topNData != null && (!topEntities || topEntities.length === 0)))
    ) {
      return undefined;
    }
    // The nested view reports Success off the parent totals alone, so emitting
    // the carried-over payload here would draw the previous view's cells under
    // the new groups. Withhold it and let the consumer render group skeletons.
    if (isChildDataPending) {
      return undefined;
    }
    return shapedChildData;
  })();

  return (
    <>
      {children({
        data: finalData,
        monthRefs,
        status,
        parentLookup,
        parentTotals,
        parentTotalsByEntity,
        isNested,
        isChildDataPending,
        displayLevelId,
        isDisplayLeaf,
        parentLevelId,
      })}
    </>
  );
}
